// src/pages/AccountDetail.tsx
import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Typography, Paper, Box, Alert, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, Grid, SxProps, Theme
} from '@mui/material';
import { useParams, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';

interface AccountInfo {
  id: string;
  name: string;
  phone_number: string;
  status: string;
  meta_info?: string | null;
  busy?: {
    worker_type: string;
    job_id: string;
    locked_at: number;
  } | null;
}

interface UsageItem {
  use_proxy: string;
  usage_time: string;
  usage_type: string;
}

interface ProxyInfo {
  id: string;
  host: string;
  port: number;
  proxy_type: string;
  is_active: boolean;
}

interface AccountStats {
  id: string;
  account_id: string;
  usage: UsageItem[];
}

interface ProxyListResponse {
  proxys: any[]; // или ProxyInfo[] если типизация полная
  total_count: number;
}

const gridItem: SxProps<Theme> = { display: 'flex', alignItems: 'center', width: '100%' };

const AccountDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [stats, setStats] = useState<AccountStats | null>(null);
  const [proxyMap, setProxyMap] = useState<Record<string, ProxyInfo>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState<{ text: string; type: 'error' | 'success' | 'info' } | null>(null);
  const auth = useContext(AuthContext);
  const TIMEOUT = 10000;

  useEffect(() => {
    const fetchData = async () => {
      if (!auth?.isAuthenticated || !id) return;
      setIsLoading(true);
      try {
        // 1. Получаем информацию об аккаунте + статистику
        const [accRes, statsRes] = await Promise.all([
          api.get(`/accounts/${id}`, { timeout: TIMEOUT }),
          api.get(`/accounts/stats/${id}`, { timeout: TIMEOUT })
        ]);

        setAccount(accRes.data);
        setStats(statsRes.data);

        // 2. Собираем уникальные proxy ID из usage
        const proxyIds: string[] = Array.from(
          new Set(statsRes.data.usage.map((u: UsageItem) => u.use_proxy))
        );

        if (proxyIds.length === 0) {
          setIsLoading(false);
          return;
        }

        // 3. Получаем список прокси (теперь это объект { proxys: [], total_count })
        const proxyRes = await api.get<ProxyListResponse>('/proxy', { timeout: TIMEOUT });

        // ← Вот исправление: работаем с proxyRes.data.proxys (массив)
        const proxyDict: Record<string, ProxyInfo> = {};
        proxyRes.data.proxys.forEach((p: any) => {
          proxyDict[p.id] = {
            id: p.id,
            host: p.host,
            port: p.port,
            proxy_type: p.proxy_type,
            is_active: p.is_active
          };
        });
        setProxyMap(proxyDict);
      } catch (error: any) {
        console.error('Ошибка загрузки:', error);
        setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, auth?.isAuthenticated]);

  if (isLoading) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  if (!account) {
    return (
      <Container sx={{ py: 6 }}>
        <Alert severity="error">Аккаунт не найден</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {/* === ХЛЕБНЫЕ КРОШКИ === */}
      <Typography sx={{ mb: 3, fontSize: '0.9rem' }}>
        <Link to="/accounts" style={{ color: '#6B7280', textDecoration: 'none' }}>
          Аккаунты
        </Link>
        {' > '}
        <Box component="span" sx={{ color: '#1E3A8A', fontWeight: 500 }}>
          {account.name}
        </Box>
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* === ОСНОВНАЯ ИНФОРМАЦИЯ === */}
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2, mb: 4 }}>
        <Typography variant="h5" sx={{ color: '#1E3A8A', fontWeight: 600, mb: 3 }}>
          Информация об аккаунте
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ ...gridItem, justifyContent: 'space-between' }}>
              <Typography sx={{ color: '#4B5563', fontWeight: 500 }}>Имя:</Typography>
              <Typography sx={{ color: '#1F2937', ml: 2 }}>{account.name}</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ ...gridItem, justifyContent: 'space-between' }}>
              <Typography sx={{ color: '#4B5563', fontWeight: 500 }}>Телефон:</Typography>
              <Typography sx={{ color: '#1F2937', ml: 2, fontFamily: 'monospace' }}>{account.phone_number}</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ ...gridItem, justifyContent: 'space-between' }}>
              <Typography sx={{ color: '#4B5563', fontWeight: 500 }}>Статус:</Typography>
              <Chip
                label={account.status === 'AUTHORIZED' ? 'Активен' : 'Неактивен'}
                size="small"
                color={account.status === 'AUTHORIZED' ? 'success' : 'error'}
                sx={{ ml: 2 }}
              />
            </Box>
          </Grid>
          {account.meta_info && (
            <Grid size={{ xs: 12 }}>
              <Box sx={{ ...gridItem, flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography sx={{ color: '#4B5563', fontWeight: 500, mb: 1 }}>Доп. информация:</Typography>
                <Typography sx={{ color: '#1F2937', fontSize: '0.9rem', bgcolor: '#F3F4F6', p: 1.5, borderRadius: 1, width: '100%' }}>
                  {account.meta_info}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {account.busy && (
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2, mb: 4, border: '2px dashed #F59E0B', bgcolor: '#FFFBEB' }}>
          <Typography variant="h6" sx={{ color: '#92400E', fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            Аккаунт занят задачей
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#92400E', fontWeight: 500 }}>Тип задачи:</Typography>
                <Typography sx={{ fontWeight: 600, color: '#B45309' }}>
                  {account.busy.worker_type}
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#92400E', fontWeight: 500 }}>ID задачи:</Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.9rem', color: '#B45309' }}>
                  {account.busy.job_id.slice(0, 8)}...
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#92400E', fontWeight: 500 }}>Заблокирован:</Typography>
                <Typography sx={{ color: '#B45309' }}>
                  {new Date(account.busy.locked_at * 1000).toLocaleString('ru')}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Alert severity="warning" sx={{ mt: 3 }}>
            Этот аккаунт сейчас используется в задаче. Дождитесь завершения или отмените задачу.
          </Alert>
        </Paper>
      )}

      {/* === СТАТИСТИКА ИСПОЛЬЗОВАНИЯ === */}
      {stats && stats.usage.length > 0 ? (
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h5" sx={{ color: '#1E3A8A', fontWeight: 600, mb: 3 }}>
            История использования
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Время</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Тип действия</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Прокси</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.usage.map((item, idx) => {
                const proxy = proxyMap[item.use_proxy];
                return (
                  <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#F9FAFB' } }}>
                    <TableCell sx={{ fontSize: '0.85rem', color: '#4B5563' }}>
                      {new Date(item.usage_time).toLocaleString('ru')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.usage_type}
                        size="small"
                        sx={{ bgcolor: '#EBF8FF', color: '#1E40AF' }}
                      />
                    </TableCell>
                    <TableCell>
                      {proxy ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {proxy.host}:{proxy.port}
                          </Typography>
                          <Chip
                            label={proxy.proxy_type}
                            size="small"
                            color={proxy.proxy_type === 'MTPROTO' ? 'success' : proxy.proxy_type === 'SOCKS5' ? 'primary' : 'default'}
                          />
                          {!proxy.is_active && (
                            <Chip label="Выключен" size="small" color="error" />
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF' }}>Прокси удалён</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography sx={{ color: '#6B7280' }}>Нет записей об использовании</Typography>
        </Paper>
      )}
    </Container>
  );
};

export default AccountDetail;